"use client";

import React, { useState, useEffect } from "react";
import { Menu, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
import { MenuProvider, useMenu, MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
interface NavItemProps {
  item: MenuItem;
  depth: number;
  isMobile?: boolean;
  onClose?: () => void;
}
function NavItemWithChildren({
  item,
  depth,
  isMobile,
  onClose
}: NavItemProps) {
  const hasChildren = item.children && item.children.length > 0;
  const [isOpen, setIsOpen] = useState(false);
  const [openSecondLevel, setOpenSecondLevel] = useState<number | null>(null);
  if (isMobile) {
    return <div className="flex flex-col w-full">
        {hasChildren ? <>
            <Button variant="ghost" onClick={() => setIsOpen(!isOpen)} className="flex items-center justify-between w-full py-2 px-0 text-sm font-serif font-medium text-foreground hover:text-primary transition-colors text-left h-auto bg-transparent hover:bg-transparent" style={{
          paddingLeft: depth * 16
        }}>
              {item.label}
              <ChevronDown className={`size-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
            </Button>
            {isOpen && <div className="flex flex-col">
                {item.children?.map((child, i) => <div key={child.id} data-index={i}>
                    <NavItemWithChildren item={child} depth={depth + 1} isMobile={true} onClose={onClose} />
                  </div>)}
              </div>}
          </> : <Link to={item.href || "#"} className={`${depth > 0 ? "text-muted-foreground" : "font-medium text-foreground"} font-serif text-sm hover:text-primary transition-colors block py-2`} style={{
        paddingLeft: depth * 16
      }} onClick={onClose}>
            {item.label}
          </Link>}
      </div>;
  }
  return <NavigationMenuItem>
      {hasChildren ? <>
          <NavigationMenuTrigger className="font-serif font-medium text-foreground bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent p-0 h-auto text-sm transition-colors hover:text-primary">
            {item.label}
          </NavigationMenuTrigger>
          <NavigationMenuContent className="min-w-[260px] bg-background border border-border p-2 rounded-md shadow-lg">
            <ul className="flex flex-col gap-1">
              {item.children?.map((child, i) => <li key={child.id} data-index={i}>
                  {child.children && child.children.length > 0 ? <div className="w-full">
                      <Button variant="ghost" onClick={() => setOpenSecondLevel(openSecondLevel === i ? null : i)} className="pl-4 py-2 text-sm font-medium cursor-pointer hover:bg-accent hover:text-primary rounded-md transition-colors inline-flex items-center justify-between w-full text-left h-auto">
                        {child.label}
                        <ChevronDown className={`size-3 transition-transform duration-200 ${openSecondLevel === i ? "rotate-180" : ""}`} />
                      </Button>
                      {openSecondLevel === i && <ul className="flex flex-col mt-1">
                          {child.children.map((grandChild, j) => <li key={grandChild.id} data-index={j}>
                              <Link to={grandChild.href || "#"} className="pl-8 py-2 px-4 text-sm block w-full text-left hover:bg-accent hover:text-primary transition-colors rounded-md" onClick={() => setOpenSecondLevel(null)}>
                                {grandChild.label}
                              </Link>
                            </li>)}
                        </ul>}
                    </div> : <NavigationMenuLink asChild>
                      <Link to={child.href || "#"} className="block w-full px-4 py-2 text-sm font-serif font-medium text-foreground hover:bg-accent hover:text-primary transition-colors rounded-md">
                        {child.label}
                      </Link>
                    </NavigationMenuLink>}
                </li>)}
            </ul>
          </NavigationMenuContent>
        </> : <NavigationMenuLink asChild>
          <Link to={item.href || "#"} className={`font-serif font-medium text-foreground text-sm transition-colors hover:text-primary ${depth === 0 ? "relative py-2 after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-0 after:-translate-x-1/2 after:bg-primary after:transition-all after:duration-300 hover:after:w-full" : "block w-full px-4 py-2 hover:bg-accent rounded-md"}`}>
            {item.label}
          </Link>
        </NavigationMenuLink>}
    </NavigationMenuItem>;
}
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <div key={item.id} data-index={i}>
            <NavItemWithChildren item={item} depth={0} />
          </div>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground">
          <Menu className="size-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background">
        <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
        <div className="flex flex-col gap-8 mt-8">
          <Link to="/" onClick={() => setIsOpen(false)}>
            <WvcLogo className="h-7 w-auto" />
          </Link>
          {loading ? <div className="flex flex-col gap-4">
              {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-8 w-full bg-muted animate-pulse rounded-md" />)}
            </div> : <nav className="flex flex-col gap-2">
              {menuItems.map((item, i) => <div key={item.id} data-index={i}>
                  <NavItemWithChildren item={item} depth={0} isMobile={true} onClose={() => setIsOpen(false)} />
                </div>)}
              <Button asChild className="rounded-md bg-primary text-primary-foreground w-full mt-4 shadow-md hover:bg-primary/90">
                <Link to="#" onClick={() => setIsOpen(false)}>Donate</Link>
              </Button>
            </nav>}
        </div>
      </SheetContent>
    </Sheet>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border" : "bg-background border-b border-transparent"}`}>
      <header className="container mx-auto px-4 h-20 flex items-center justify-between">
        <div className="flex items-center">
          <Link to="/">
            <WvcLogo className="h-7 w-auto" />
          </Link>
        </div>

        <div className="flex items-center gap-8">
          <MenuProvider menu_id="47">
            <DesktopNav />
          </MenuProvider>

          <Button asChild className="hidden md:inline-flex rounded-md bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
            <Link to="#">Donate</Link>
          </Button>

          <MenuProvider menu_id="47">
            <MobileNav />
          </MenuProvider>
        </div>
      </header>
    </section>;
}